import {TableIcon} from '@primer/octicons-react'
import {Fragment} from 'react'

import {LABELS} from '../constants/labels'
import type {RemovedFromProjectV2EventNode} from './issue-viewer-page-query-types'
import {ProjectV2} from './ProjectV2'
import styles from './RemovedFromProjectV2Event.module.css'
import {RolledupProjectV2Event} from './RolledupProjectV2Event'
import {TimelineRow} from './row/TimelineRow'

export type RemovedFromProjectV2EventFragment = Pick<RemovedFromProjectV2EventNode, 'actor' | 'createdAt' | 'project'>

type RemovedFromProjectV2EventProps = {
  queryRef: RemovedFromProjectV2EventFragment
  issueUrl: string
  onLinkClick?: (event: MouseEvent) => void
  rollupGroup?: Record<string, RemovedFromProjectV2EventFragment[]>
}

export function RemovedFromProjectV2Event({
  queryRef,
  issueUrl,
  onLinkClick,
  rollupGroup,
}: RemovedFromProjectV2EventProps): React.ReactElement {
  const {actor, createdAt, project} = queryRef

  if (!project) {
    return <></>
  }

  // Once we have support for the project IDs being exposed in GQL we can shift to using the
  // `issueEventExternalUrl` function here for our deep linked timestamp.
  return (
    <TimelineRow
      highlighted={false}
      actor={actor}
      onLinkClick={onLinkClick}
      createdAt={createdAt}
      deepLinkUrl={issueUrl}
      leadingIcon={TableIcon}
    >
      <TimelineRow.Main>
        {rollupGroup ? (
          <RolledupProjectV2Event rollupGroup={rollupGroup} />
        ) : (
          <RemovedFromProjectV2sRendering queryRefs={[queryRef]} />
        )}
      </TimelineRow.Main>
    </TimelineRow>
  )
}

export const RemovedFromProjectV2sRendering = ({queryRefs}: {queryRefs: RemovedFromProjectV2EventFragment[]}) => {
  if (queryRefs.length === 0) {
    return null
  }

  return (
    <>
      {`${LABELS.timeline.removedThisFrom} `}
      {queryRefs.map((queryRef, index) => (
        // eslint-disable-next-line @eslint-react/no-array-index-key
        <Fragment key={index}>
          <InternalRemovedFromProjectV2sRendering
            queryRef={queryRef}
            first={index === 0}
            last={index === queryRefs.length - 1}
          />
        </Fragment>
      ))}
    </>
  )
}

const InternalRemovedFromProjectV2sRendering = ({
  queryRef,
  first,
  last,
}: Pick<RemovedFromProjectV2EventProps, 'queryRef'> & {
  first: boolean
  last: boolean
}) => {
  const {project} = queryRef

  if (!project?.title || !project?.url) {
    return null
  }

  return (
    <span className={styles.Text}>
      <>
        {!first && !last && <span className={styles.Text_1}>,</span>}
        {!first && last && <span className={styles.Text_2}>{LABELS.timeline.and}</span>}
        <ProjectV2 title={project?.title} url={project?.url} />
      </>
    </span>
  )
}
